"use client";

import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";

const capabilities = [
    {
        title: "AI Systems",
        description: "We build intelligent systems that automate real work. From LLM orchestration to custom agentic workflows."
    },
    {
        title: "Product Engineering",
        description: "We design and ship production-ready products. Scalable, secure, and built for high-value execution."
    },
    {
        title: "Web3 & Infra",
        description: "Scalable systems built for real usage. Protocol design, smart contracts, and high-performance infrastructure."
    },
    {
        title: "Design & Experience",
        description: "High-fidelity interfaces that prioritize clarity and flow. We create digital experiences that feel premium and effortless."
    }
];

export const CapabilitiesSection = () => {
    return (
        <section className="relative py-40 px-4 bg-background text-foreground overflow-hidden">
            <div className="max-w-[1400px] mx-auto space-y-32">
                <div className="max-w-2xl">
                    <span className="micro-id tracking-[0.4em] text-primary/60 uppercase">High-Value Expertise</span>
                    <h2 className="font-display text-7xl md:text-8xl leading-[0.9] mt-8 uppercase tracking-tighter">
                        Built for those <br />
                        who scale.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-32">
                    {capabilities.map((cap, index) => (
                        <motion.div 
                            key={cap.title}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="space-y-8 group"
                        >
                            <div className="flex items-baseline gap-4">
                                <span className="font-mono text-xs opacity-30">0{index + 1}</span>
                                <h3 className="font-display text-5xl md:text-6xl uppercase group-hover:text-primary transition-colors duration-500">
                                    {cap.title}
                                </h3>
                            </div>
                            <p className="font-sans text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-lg">
                                {cap.description}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Explore More Button */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="pt-12"
                >
                    <Link 
                        href="/services" 
                        className="group inline-flex items-center gap-8 group"
                    >
                        <div className="relative">
                            <span className="font-display text-3xl md:text-4xl uppercase tracking-tighter group-hover:text-primary transition-colors duration-500">
                                Explore <br /> All Services
                            </span>
                            <div className="absolute -bottom-2 left-0 w-full h-[1px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                        </div>
                        <div className="w-16 h-16 rounded-full border border-foreground/10 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-all duration-500 -rotate-45 group-hover:rotate-0">
                            <svg className="w-6 h-6 transition-transform duration-500 group-hover:text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </Link>
                </motion.div>
            </div>
            
            {/* Subtle premium visual detail: Background grid */}
            <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]">
                <div className="h-full w-full bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:100px_100px]" />
            </div>
        </section>
    );
};
